import { Alert, Breadcrumb, Button, ButtonGroup, ButtonGroupItem, Checkbox, Dropdown, Form, Input } from '@boozallen-uip/uswds-react-core';
import React, { useEffect, useCallback, useContext, useRef, useState } from 'react';
import tw from 'tailwind-styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { SectionContainer } from '../shared.styled';
import useAccount from '../../hooks/useAccount.hook';
import ExternalLinkIcon from '../../components/ExternalLinkIcon.component';
import {VendorContext} from '../Vendor/VendorContext';
import { BASE_TITLE, HOME_URL, REACT_APP_ACR_API_BASE_URL, REACT_APP_ACR_API_PROTOCOL, WELCOME_URL, ACCESS_LEVEL_USER, TERMS_OF_USE_URL } from "../../utlities/core.constants";

const OptionContainer = tw.div`border-[1px] border-solid border-black px-3 flex radius-md mb-4`;
const OptionContentContainer = tw.div`flex-1 mt-3 [&>p]:text-sm`;
const OptionRadioContainer = tw.div`pt-3 pr-3 flex items-start`;
const OptionTitleContainer = tw.div``;

const TERMS_AGREEMENT_REQUIRED_MESSAGE = "Please agree to the Terms of Use before submitting.";

function FormFieldRequired() {
  return <span className='text-red'>(Required)</span>
}

export default function FirstTimeLogin() {
  const [vendorId, setVendorId ] = useState(null);
  const [agencyId, setAgencyId ] = useState(null);
  const [name, setName] = useState("");
  const [comments, setComments] = useState("");
  const [requestedAccessLevel, setRequestedAccessLevel] = useState(null);
  const [entityType, setEntityType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitErrorType, setSubmitErrorType] = useState("");
  const [validationError, setValidationError] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const bannerRef = useRef(null);
  const { vendors } = useContext(VendorContext);
  const [agencies, setAgencies] = useState([]);
  const navigate = useNavigate();
  const { submitUserAccountRequest } = useAccount();
  const hasSelectedEntityType = Boolean(entityType);
  const hasSelectedAccessLevel = Boolean(requestedAccessLevel);
  const isVendorSelection = entityType === "vendor";
  const alertType = validationError || submitErrorType === "submit" ? "error" : "warning";
  let alertHeading = "Redirect issue";
  if (validationError) {
    alertHeading = "Missing Information";
  } else if (submitErrorType === "submit") {
    alertHeading = "Unable to Submit";
  }
  const generalAccessLabel = isVendorSelection ? "Editor" : "User";
  const generalAccessDescription = isVendorSelection
    ? "Ability to create, edit, submit, and browse ACRs for your organization."
    : "Ability to browse and view published products and ACRs.";
  const banner = (validationError || submitError)
    ? {
      heading: alertHeading,
      type: alertType,
      message: validationError || submitError
    }
    : null;

  document.title = `${BASE_TITLE} - First Time Login`;

  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        const response = await fetch(`${REACT_APP_ACR_API_PROTOCOL}://${REACT_APP_ACR_API_BASE_URL}/api/agency/all`);
        const data = await response.json();
        setAgencies(data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching agencies', error);
      }
    }

    fetchAgencies();
  }, []);

  useEffect(() => {
    if (!banner || !bannerRef.current) {
      return;
    }
    bannerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [banner]);

  const onSubmitHandler = useCallback(async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setSubmitError("");
    setSubmitErrorType("");
    setValidationError("");

    const trimmedName = name.trim();
    if (!trimmedName) {
      setValidationError("Enter your full name to continue.");
      return;
    }

    if (!agreedToTerms) {
      setValidationError(TERMS_AGREEMENT_REQUIRED_MESSAGE);
      return;
    }

    setIsSubmitting(true);
    let requestSubmitted = false;

    try {
      await submitUserAccountRequest({
        vendorId,
        agencyId,
        name: trimmedName,
        additionalComments: comments,
        requestedAccessLevel
      });
      requestSubmitted = true;
      navigate(WELCOME_URL, { replace: true });
    } catch (error) {
      if (requestSubmitted) {
        setSubmitError("Your request was submitted, but we couldn't redirect you. Please open Account Home from the account menu.");
        setSubmitErrorType("redirect");
      } else {
        setSubmitError("We couldn't submit your request right now. Please try again.");
        setSubmitErrorType("submit");
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [agencyId, agreedToTerms, comments, isSubmitting, name, navigate, requestedAccessLevel, submitUserAccountRequest, vendorId]);

  const vendorChangeHandler = (e) => {
    if(!e.detail.value){
      return;
    }
    const { id } = e.detail.value;
    setVendorId(id);
  };

  const agencyChangeHandler = (e) => {
    if(!e.detail.value){
      return;
    }
    const { id } = e.detail.value;
    setAgencyId(id);
  };

  // onClick vendor radio button will use this to set entityType to vendor
  const handleVendorSelect = () => {
    setEntityType("vendor");
    setAgencyId(null);
  }

  // onClick agency radio button will use this to set entityType to agency
  const handleAgencySelect = () => {
    setEntityType("agency");
    setVendorId(null);
  }

  return <SectionContainer>
    <Breadcrumb links={[{ href: HOME_URL, text: "Home" }, { href: WELCOME_URL, text: "Welcome" }]} />
    <section>
      <h1 className="text-primary-light text-3xl mb-2">
        Welcome to Your ACR Repository Account
      </h1>
      <div>
        <p>Before you gain full access, you’ll need to be aligned to either a vendor or a buyer account.</p>
        <p>Get started by making selections below and hit “Submit”. This will route your request to the appropriate POC and you will be granted full access shortly.</p>
      </div>
    </section>
    <div ref={bannerRef} className="mb-4 w-full max-w-4xl">
      {banner ? (
        <Alert
          heading={banner.heading || (banner.type === "error" ? "Error" : "Success")}
          type={banner.type || "success"}
          onClose={() => {
            setValidationError("");
            setSubmitError("");
            setSubmitErrorType("");
          }}
        >
          {banner.message}
        </Alert>
      ) : null}
    </div>
    <Form className='grid grid-cols-12 max-w-full' onSubmit={onSubmitHandler}>
      <section className='col-start-1 col-span-6'>
        <h2 className='text-xl mb-6'>How Will You Use ACR Repository</h2>
        <fieldset className="m-0 border-0 p-0">
          <legend id="entity-type-label" className='mb-2'>I am a: <FormFieldRequired /></legend>
          <OptionContainer className='cursor-pointer' onClick={handleAgencySelect}>
            <OptionRadioContainer>
              <input
                id="entity-type-buyer"
                type="radio"
                name="buyer_group"
                value="agency"
                checked={entityType === "agency"}
                onChange={handleAgencySelect}
                aria-label="Buyer"
                aria-describedby="entity-type-buyer-desc-1 entity-type-buyer-desc-2"
                className="mt-1"
              />
            </OptionRadioContainer>
            <OptionContentContainer>
              <OptionTitleContainer id="entity-type-buyer-title">Buyer</OptionTitleContainer>
              <p id="entity-type-buyer-desc-1">Select this option if you will be using ACRs to make purchasing decisions. </p>
              <p id="entity-type-buyer-desc-2">Your request will be routed to your government agency POC for approval.</p>
            </OptionContentContainer>
          </OptionContainer>
          <OptionContainer className='cursor-pointer' onClick={handleVendorSelect}>
            <OptionRadioContainer>
              <input
                id="entity-type-vendor"
                type="radio"
                name="buyer_group"
                value="vendor"
                checked={entityType === "vendor"}
                onChange={handleVendorSelect}
                aria-label="Vendor"
                aria-describedby="entity-type-vendor-desc-1 entity-type-vendor-desc-2"
                className="mt-1"
              />
            </OptionRadioContainer>
            <OptionContentContainer>
              <OptionTitleContainer id="entity-type-vendor-title">Vendor</OptionTitleContainer>
              <p id="entity-type-vendor-desc-1">Select this option if you will be submitting your product’s ACRs and/or managing their overview pages. </p>
              <p id="entity-type-vendor-desc-2">Your request will be routed to your company’s POC for approval.</p>
            </OptionContentContainer>
          </OptionContainer>
        </fieldset>
      </section>
      {hasSelectedEntityType ? (
        <section className='col-start-1 col-span-6'>
          <h2 className='text-xl mb-6'>Account Level</h2>
          <fieldset className="m-0 border-0 p-0">
            <legend id="access-level-label" className='mb-2'>I am requesting: <FormFieldRequired /></legend>
            <OptionContainer className='cursor-pointer' onClick={() => setRequestedAccessLevel(ACCESS_LEVEL_USER)}>
              <OptionRadioContainer>
                <input
                  id="access-level-user"
                  type="radio"
                  name="access_level_group"
                  value={ACCESS_LEVEL_USER}
                  checked={requestedAccessLevel === ACCESS_LEVEL_USER}
                  onChange={() => setRequestedAccessLevel(ACCESS_LEVEL_USER)}
                  aria-label={generalAccessLabel}
                  aria-describedby="access-level-user-desc"
                  className="mt-1"
                />
              </OptionRadioContainer>
              <OptionContentContainer>
                <OptionTitleContainer id="access-level-user-title">{generalAccessLabel}</OptionTitleContainer>
                <p id="access-level-user-desc">{generalAccessDescription}</p>
              </OptionContentContainer>
            </OptionContainer>
            <OptionContainer className='cursor-pointer' onClick={() => setRequestedAccessLevel('ADMIN')}>
              <OptionRadioContainer>
                <input
                  id="access-level-admin"
                  type="radio"
                  name="access_level_group"
                  value="ADMIN"
                  checked={requestedAccessLevel === 'ADMIN'}
                  onChange={() => setRequestedAccessLevel('ADMIN')}
                  aria-label="Admin"
                  aria-describedby="access-level-admin-desc"
                  className="mt-1"
                />
              </OptionRadioContainer>
              <OptionContentContainer>
                <OptionTitleContainer id="access-level-admin-title">Admin</OptionTitleContainer>
                <p id="access-level-admin-desc">Admin-level access. Allows you to manage users for your organization.</p>
              </OptionContentContainer>
            </OptionContainer>
          </fieldset>
        </section>
      ) : null}
      {hasSelectedAccessLevel ? (
        <>
          <section className='col-start-1 col-span-4'>
          <h2 className='text-xl mb-6'>Requester Information</h2>
            <div>
              <Input
            id="requester-full-name"
            value={name}
            required
            onChange={(e) => {
              setName(e.target.value);
              setValidationError("");
            }}
            onKeyDown={(event) => {
              // The submit button is disabled until the box is checked, so Enter
              // never fires submit. Surface the same guard the submit handler uses.
              if (event.key !== "Enter" || agreedToTerms || isSubmitting) {
                return;
              }
              event.preventDefault();
              setSubmitError("");
              setSubmitErrorType("");
              setValidationError(TERMS_AGREEMENT_REQUIRED_MESSAGE);
            }}
            type="text"
          >
            Full name: <FormFieldRequired />
          </Input>
            </div>
            <div>
              <Dropdown // give this dropdown options based on wether buyer or vendor is selected
                id="organization-selection"
                value="pro"
                optionRenderer={o => o.name}
                options={entityType === "vendor" ? vendors : agencies}
                onChange={(e) => entityType === "vendor" ? vendorChangeHandler(e): agencyChangeHandler(e)}
              >
                I work for:  <FormFieldRequired />
              </Dropdown>
            </div>
            <div>
              <Input id="requester-additional-comments" onChange={(e) => setComments(e.target.value)} type='textarea'>Additional comments:</Input>
            </div>
          </section>
          <section className='col-start-1 col-span-4 mb-4'>
            <Checkbox
              id="terms-of-use-agreement"
              checked={agreedToTerms}
              onChange={(e) => {
                const nextAgreed = e.target.checked;
                setAgreedToTerms(nextAgreed);
                if (nextAgreed && validationError === TERMS_AGREEMENT_REQUIRED_MESSAGE) {
                  setValidationError("");
                }
              }}
              aria-label='By selecting "Submit", you agree to our Terms of Use'
            >
              By selecting &quot;Submit&quot;, you agree to our{" "}
              <Link to={TERMS_OF_USE_URL} target="_blank" rel="noopener noreferrer">
                Terms of Use
                <ExternalLinkIcon />
                <span className="usa-sr-only"> (opens in new window)</span>
              </Link>.
            </Checkbox>
          </section>
          <section className='col-start-1 col-span-2'>
            <ButtonGroup>
              <ButtonGroupItem>
                <Button type='submit' disabled={isSubmitting || !agreedToTerms}>
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </ButtonGroupItem>
            </ButtonGroup>
          </section>
        </>
      ) : null}
    </Form>

  </SectionContainer>
}
